import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { Button, Modal, ResponsiveImage } from '../../../components';
import IconCollection from '../../../components/IconCollection/IconCollection';

import css from './DifferentOwnerCartModal.module.css';

const MODAL_ID = 'DifferentOwnerCartModal';

export const DIFFERENT_OWNER_ACTION_START_NEW_CART = 'startNewCart';
export const DIFFERENT_OWNER_ACTION_SELECT_MORE = 'selectMore';

// Square thumbnails both sides already carry: the cart's denormalized listings
// (cart.duck.js's getItemsByAuthor) and the listing page's own listing are fetched
// with these variants, so no extra image request is needed.
const CARD_IMAGE_VARIANTS = ['listing-card', 'listing-card-2x'];

// One side of the "In your cart" / "Adding now" comparison.
const ComparisonCard = ({ labelId, image, title, ownerName, tone }) => (
  <div className={classNames(css.card, tone === 'new' ? css.cardNew : css.cardExisting)}>
    <span className={css.cardLabel}>
      <FormattedMessage id={labelId} />
    </span>
    <div className={css.cardImage}>
      {image ? (
        <ResponsiveImage
          rootClassName={css.cardImg}
          image={image}
          variants={CARD_IMAGE_VARIANTS}
          alt={title || ''}
        />
      ) : (
        <i className={classNames('ri-image-line', css.cardImagePlaceholder)} aria-hidden="true" />
      )}
    </div>
    <span className={css.cardTitle}>
      {title || <FormattedMessage id="DifferentOwnerCartModal.unknownItem" />}
    </span>
    {ownerName ? <span className={css.cardOwner}>{ownerName}</span> : null}
  </div>
);

/**
 * "New cart needed" confirmation (RAS-2622, redesigned 2026-09-18 to the client's mock).
 *
 * Shown on a marketplace listing page when Rent Now would create a brand-new per-store
 * cart next to one the shopper started this session with another store. Purely
 * presentational: the caller decides WHEN it applies (shouldPromptForDifferentOwner,
 * util/cartHelpers.js) and owns the add itself; this component only renders the
 * comparison - the item already in the cart against the one being added, each with
 * its owner - and routes the three choices.
 *
 * Bottom sheet on phones, compact centered dialog from tablet up - the same Modal
 * overrides as StoreProfilePage/StoreSearchScopeModal. Every dismissal (X, Esc) is the
 * same "nothing added" outcome as Continue Browsing.
 *
 * While an add is in flight (`inProgress` names the pressed action) every control is
 * disabled - including the X - so a second tap can't start a second add, and closing
 * can't leave one running invisibly behind a dismissed sheet.
 */
const DifferentOwnerCartModal = props => {
  const {
    isOpen,
    existingItemTitle,
    existingItemImage,
    existingStoreName,
    newItemTitle,
    newItemImage,
    newStoreName,
    inProgress,
    onClose,
    onStartNewCart,
    onContinueBrowsing,
    onSelectMoreItems,
    onManageDisableScrolling,
  } = props;

  const titleId = `${MODAL_ID}-title`;
  const descriptionId = `${MODAL_ID}-description`;
  const isBusy = !!inProgress;

  // Modal's own handleClose releases the body scroll lock BEFORE calling onClose. When
  // a dismissal is refused mid-add, re-assert the lock so the page behind the still-open
  // sheet doesn't become scrollable.
  const handleClose = event => {
    if (isBusy) {
      onManageDisableScrolling(MODAL_ID, true);
      return;
    }
    onClose(event);
  };

  return (
    <Modal
      id={MODAL_ID}
      isOpen={isOpen}
      onClose={handleClose}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
      className={css.root}
      scrollLayerClassName={css.scrollLayer}
      containerClassName={css.container}
      contentClassName={css.content}
      closeButtonClassName={isBusy ? css.closeButtonDisabled : css.closeButton}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-busy={isBusy}
        className={css.dialog}
      >
        <div className={css.dragHandleArea} aria-hidden="true">
          <span className={css.dragHandle} />
        </div>
        <div className={css.header}>
          <div className={css.iconCircle} aria-hidden="true">
            <IconCollection name="cart" className={css.icon} />
          </div>
          <h2 id={titleId} className={css.title}>
            <FormattedMessage id="DifferentOwnerCartModal.title" />
          </h2>
        </div>
        <p id={descriptionId} className={css.description}>
          <FormattedMessage id="DifferentOwnerCartModal.description" />
        </p>
        <div className={css.comparison}>
          <ComparisonCard
            labelId="DifferentOwnerCartModal.inYourCart"
            image={existingItemImage}
            title={existingItemTitle}
            ownerName={existingStoreName}
            tone="existing"
          />
          <span className={css.notEqual} aria-hidden="true">
            ≠
          </span>
          <ComparisonCard
            labelId="DifferentOwnerCartModal.addingNow"
            image={newItemImage}
            title={newItemTitle}
            ownerName={newStoreName}
            tone="new"
          />
        </div>
        <div className={css.actions}>
          <div className={css.actionRow}>
            <Button
              type="button"
              variant="secondary-outline"
              className={css.actionButton}
              disabled={isBusy}
              inProgress={inProgress === DIFFERENT_OWNER_ACTION_SELECT_MORE}
              onClick={onSelectMoreItems}
            >
              <span className={css.twoLineLabel}>
                <span className={css.twoLineTitle}>
                  <FormattedMessage id="DifferentOwnerCartModal.selectMoreItems" />
                </span>
                <span className={css.twoLineSubtitle}>
                  {/* Names the store: this button sits under the "In your cart" card, and a
                      bare "from this owner" read as the OTHER store (tester feedback
                      2026-09-22). It is the store being added to - the shopper lands
                      there with the new cart open. */}
                  <FormattedMessage
                    id="DifferentOwnerCartModal.selectMoreItemsSub"
                    values={{ newStoreName }}
                  />
                </span>
              </span>
            </Button>
            <Button
              type="button"
              variant="secondary-outline"
              className={css.actionButton}
              disabled={isBusy}
              inProgress={inProgress === DIFFERENT_OWNER_ACTION_START_NEW_CART}
              onClick={onStartNewCart}
            >
              <span className={css.twoLineLabel}>
                <span className={css.twoLineTitle}>
                  <FormattedMessage id="DifferentOwnerCartModal.startNewCart" />
                </span>
                <span className={css.twoLineSubtitle}>
                  <FormattedMessage id="DifferentOwnerCartModal.startNewCartSub" />
                </span>
              </span>
            </Button>
          </div>
          <Button
            type="button"
            variant="secondary-outline"
            className={classNames(css.actionButton, css.actionButtonWide)}
            disabled={isBusy}
            onClick={onContinueBrowsing}
          >
            <FormattedMessage id="DifferentOwnerCartModal.continueBrowsing" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

DifferentOwnerCartModal.defaultProps = {
  existingItemTitle: '',
  existingItemImage: null,
  existingStoreName: '',
  newItemTitle: '',
  newItemImage: null,
  newStoreName: '',
  inProgress: null,
};

DifferentOwnerCartModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  existingItemTitle: PropTypes.string,
  existingItemImage: propTypes.image,
  existingStoreName: PropTypes.string,
  newItemTitle: PropTypes.string,
  newItemImage: propTypes.image,
  newStoreName: PropTypes.string,
  inProgress: PropTypes.oneOf([
    DIFFERENT_OWNER_ACTION_START_NEW_CART,
    DIFFERENT_OWNER_ACTION_SELECT_MORE,
    null,
  ]),
  onClose: PropTypes.func.isRequired,
  onStartNewCart: PropTypes.func.isRequired,
  onContinueBrowsing: PropTypes.func.isRequired,
  onSelectMoreItems: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
};

export default DifferentOwnerCartModal;
